import React, { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';
import { messaggioDa } from '../../utils/notifiche';
import { COLORI_AGENDA } from '../../utils/coloriAgenda';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';

/**
 * FinestraAgenda — «Nuova agenda» e «Modifica» negli Orari (Alex, 4/10/2026).
 *
 * Una finestra sola per le due cose, perche' i campi sono gli stessi: nome,
 * colore, durata di default, ordine, attiva, e i codici di MioDottore. Tipo e
 * medico si scelgono solo alla nascita: un'agenda e' di chi e' nata, e i suoi
 * appuntamenti con lei.
 *
 * Le regole non stanno qui: le decide la rotta (services/agende.js), e qui si
 * mostra il suo motivo quando rifiuta. In particolare i codici di MioDottore
 * li scrive solo l'amministrazione: alla segreteria si vedono e non si
 * mandano, e se li mandasse la rotta risponderebbe 403.
 *
 * In modifica si mandano SOLO i campi cambiati: un campo che non e' cambiato
 * non si salva, e il registro delle modifiche non prende righe inutili.
 *
 * role="dialog": la terza difesa (versioneInLinea) riconosce i campi toccati
 * di una finestra aperta e non ricarica la pagina sotto le mani di chi scrive.
 *
 * Chiudere con un campo cambiato chiede prima (useSporco e useUscitaProtetta,
 * come ModulisticaWizard): il velo, la X e «Annulla» passano tutti da
 * chiudi(). Fino alla revisione del beta su f577db6 un clic sul velo chiudeva
 * e quanto scritto si perdeva senza una parola.
 */

const CODICI = [
    ['docplanner_doctor_id', 'Codice del medico'],
    ['docplanner_address_id', 'Codice dell’indirizzo'],
    ['docplanner_schedule_id', 'Codice del calendario'],
];

const nomeDelMedico = (m) => (m ? `${m.cognome}, ${m.nome}` : '');

const FinestraAgenda = ({ agenda, agende, ruolo, onChiudi, onSalvata }) => {
    const nuova = !agenda;
    const admin = ruolo === 'admin';
    const struttura = useMemo(() => (agende || []).find((a) => a.tipo === 'struttura'), [agende]);

    const [tipo, setTipo] = useState(agenda?.tipo || 'medico');
    const [medici, setMedici] = useState(null);
    const [medicoId, setMedicoId] = useState('');
    const [nome, setNome] = useState(agenda?.nome || '');
    const [nomeToccato, setNomeToccato] = useState(!nuova);
    const [colore, setColore] = useState(agenda?.colore || '');
    const [durata, setDurata] = useState(String(agenda?.durata_default_minuti ?? 30));
    const [ordine, setOrdine] = useState(agenda ? String(agenda.ordine ?? '') : '');
    const [attiva, setAttiva] = useState(agenda ? agenda.attiva !== false : true);
    const [codici, setCodici] = useState(() => Object.fromEntries(
        CODICI.map(([k]) => [k, agenda?.[k] || ''])));
    const [errore, setErrore] = useState(null);
    const [salvando, setSalvando] = useState(false);

    /* Sporco = diverso da com'era all'apertura: chi scrive e poi cancella
       non ha cambiato niente. La versione resta 0: la finestra non salva
       restando aperta, a salvataggio riuscito si smonta. */
    const sporco = useSporco({ tipo, medicoId, nome, colore, durata, ordine, attiva, codici }, 0);
    const chiedi = useUscitaProtetta(sporco);
    const chiudi = () => {
        if (salvando) return;
        if (chiedi()) onChiudi();
    };

    useEffect(() => {
        if (!nuova) return;
        api.get('/agenda/orari/agende/medici-senza-agenda')
            .then((r) => setMedici(r.data.data || []))
            .catch((e) => { setMedici([]); setErrore(messaggioDa(e, 'Non riesco a caricare i medici senza agenda.')); });
    }, [nuova]);

    /* Il nome si propone da solo finche' chi scrive non lo tocca: «Cognome,
       Nome» come le agende nate dalla migration 047. */
    useEffect(() => {
        if (!nuova || nomeToccato) return;
        if (tipo === 'struttura') setNome('Agenda della struttura');
        else setNome(nomeDelMedico((medici || []).find((m) => String(m.id) === String(medicoId))));
    }, [nuova, nomeToccato, tipo, medicoId, medici]);

    const corpo = () => {
        const tutto = {
            nome,
            colore: colore || null,
            durata_default_minuti: durata === '' ? '' : Number(durata),
            ordine: ordine === '' ? undefined : Number(ordine),
            attiva,
        };
        if (admin) CODICI.forEach(([k]) => { tutto[k] = codici[k] || null; });
        if (nuova) {
            const c = { tipo, ...tutto };
            if (tipo === 'medico') c.medico_id = medicoId ? Number(medicoId) : null;
            if (c.ordine === undefined) delete c.ordine;
            return c;
        }
        const cambiati = {};
        Object.entries(tutto).forEach(([k, v]) => {
            if (v === undefined) return;
            const prima = agenda[k] === undefined || agenda[k] === '' ? null : agenda[k];
            const dopo = v === '' ? null : v;
            if (String(prima ?? '') !== String(dopo ?? '')) cambiati[k] = v;
        });
        return cambiati;
    };

    const salva = async () => {
        setErrore(null);
        const c = corpo();
        if (!nuova && !Object.keys(c).length) { onChiudi(); return; }
        setSalvando(true);
        try {
            const r = nuova
                ? await api.post('/agenda/orari/agende', c)
                : await api.put(`/agenda/orari/agenda/${agenda.id}`, c);
            onSalvata(r.data.data);
        } catch (e) {
            setErrore(messaggioDa(e, nuova ? 'Errore nella creazione dell’agenda.' : 'Errore nella modifica dell’agenda.'));
        } finally {
            setSalvando(false);
        }
    };

    const titolo = nuova ? 'Nuova agenda' : `Modifica: ${agenda.nome}`;

    return (
        <div className="go-modale-fondo" onClick={chiudi}>
            <div
                className="go-modale go-ag-finestra"
                role="dialog"
                aria-modal="true"
                aria-label={titolo}
                data-finestra-agenda={nuova ? 'nuova' : agenda.id}
                onClick={(e) => e.stopPropagation()}
            >
                <header>
                    <h3>{titolo}</h3>
                    <button type="button" onClick={chiudi} disabled={salvando} aria-label="Chiudi"><X size={18} /></button>
                </header>

                {nuova && (
                    <fieldset>
                        <legend>Di chi è l&apos;agenda</legend>
                        <label className="go-campo-riga">
                            <input
                                type="radio"
                                name="tipo-agenda"
                                value="medico"
                                checked={tipo === 'medico'}
                                onChange={() => setTipo('medico')}
                            />
                            <span>Di un medico</span>
                        </label>
                        <label className="go-campo-riga">
                            <input
                                type="radio"
                                name="tipo-agenda"
                                value="struttura"
                                checked={tipo === 'struttura'}
                                onChange={() => setTipo('struttura')}
                                disabled={!!struttura}
                            />
                            <span>
                                Della struttura
                                {struttura && <em className="go-ag-nota"> — c&apos;è già: {struttura.nome}</em>}
                            </span>
                        </label>
                    </fieldset>
                )}

                {nuova && tipo === 'medico' && (
                    <label className="go-campo">
                        <span>Medico</span>
                        <select
                            name="medico_id"
                            value={medicoId}
                            onChange={(e) => setMedicoId(e.target.value)}
                            disabled={medici === null}
                        >
                            <option value="">{medici === null ? 'Caricamento…' : 'Scegli il medico'}</option>
                            {(medici || []).map((m) => (
                                <option key={m.id} value={m.id}>{nomeDelMedico(m)}</option>
                            ))}
                        </select>
                        {medici && !medici.length && (
                            <small className="go-ag-nota">
                                Tutti i medici attivi hanno già un&apos;agenda. Un medico nuovo si aggiunge da Medici.
                            </small>
                        )}
                    </label>
                )}

                <label className="go-campo">
                    <span>Nome (il titolo della colonna in agenda)</span>
                    <input
                        name="nome"
                        value={nome}
                        maxLength={100}
                        onChange={(e) => { setNome(e.target.value); setNomeToccato(true); }}
                    />
                </label>

                <div className="go-campo">
                    <span>Colore in agenda</span>
                    <div className="go-ag-colori">
                        {COLORI_AGENDA.map((c) => (
                            <button
                                key={c}
                                type="button"
                                className={`go-ag-colore ${colore === c ? 'scelto' : ''}`}
                                style={{ background: c }}
                                onClick={() => setColore(c)}
                                aria-label={`Colore ${c}`}
                                aria-pressed={colore === c}
                                title={c}
                            />
                        ))}
                        <button
                            type="button"
                            className={`go-ag-colore nessuno ${!colore ? 'scelto' : ''}`}
                            onClick={() => setColore('')}
                            aria-label="Nessun colore"
                            aria-pressed={!colore}
                            title="Nessuno (grigio di default)"
                        >
                            <X size={12} />
                        </button>
                    </div>
                </div>

                <div className="go-campi-doppi">
                    <label className="go-campo">
                        <span>Durata di default (minuti)</span>
                        <input
                            type="number"
                            name="durata_default_minuti"
                            min="5"
                            max="240"
                            step="5"
                            value={durata}
                            onChange={(e) => setDurata(e.target.value)}
                        />
                    </label>
                    <label className="go-campo">
                        <span>Ordine in agenda</span>
                        <input
                            type="number"
                            name="ordine"
                            min="0"
                            max="9999"
                            value={ordine}
                            placeholder={nuova ? 'in fondo' : ''}
                            onChange={(e) => setOrdine(e.target.value)}
                        />
                    </label>
                </div>

                {!nuova && (
                    <label className="go-campo-riga">
                        <input
                            type="checkbox"
                            name="attiva"
                            checked={attiva}
                            onChange={(e) => setAttiva(e.target.checked)}
                        />
                        <span>Attiva (si vede in agenda e si prenota)</span>
                    </label>
                )}

                <fieldset>
                    <legend>MioDottore</legend>
                    {!admin && (
                        <small className="go-ag-nota">Li scrive l&apos;amministrazione.</small>
                    )}
                    {CODICI.map(([k, etichetta]) => (
                        <label className="go-campo" key={k}>
                            <span>{etichetta}</span>
                            <input
                                name={k}
                                value={codici[k]}
                                maxLength={40}
                                disabled={!admin}
                                onChange={(e) => setCodici((x) => ({ ...x, [k]: e.target.value }))}
                            />
                        </label>
                    ))}
                </fieldset>

                {errore && <div className="rm-messaggio rm-messaggio-errore" role="alert">{errore}</div>}

                <footer>
                    <button type="button" className="rm-btn" onClick={chiudi} disabled={salvando}>Annulla</button>
                    <button
                        type="button"
                        className="rm-btn rm-btn-primario"
                        data-salva-agenda
                        onClick={salva}
                        disabled={salvando || (nuova && tipo === 'medico' && !medicoId)}
                    >
                        {salvando ? 'Salvataggio…' : (nuova ? 'Crea l’agenda' : 'Salva')}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default FinestraAgenda;
